import React, { useState } from 'react';
import { HelpCircle, X, ChevronRight } from 'lucide-react';

const HelpButton = ({ pageName, tips }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentTip, setCurrentTip] = useState(0);

  const defaultTips = {
    map: [
      {
        title: "Search by Location",
        description: "Enter a ZIP code or city name to find accessible venues in that area. You can also use 'Use My Location' for nearby results."
      },
      {
        title: "Toggle Views",
        description: "Switch between Map View and List View using the toggle button. List View is great for quickly scanning venue details."
      },
      {
        title: "Understanding Badges",
        description: "'Verified ✓' means officially confirmed accessibility. 'Community Reported' means submitted by other parents. Both are valuable!"
      },
      {
        title: "Family Reviews",
        description: "Click on any venue to see detailed reviews from other parents. Their experiences can help you plan better."
      }
    ],
    events: [
      {
        title: "Filter by Type",
        description: "Use the event type dropdown to filter by sensory-friendly, adaptive sports, support groups, and more."
      },
      {
        title: "Search Your Area",
        description: "Enter your ZIP code to find events near you. Adjust the radius to expand or narrow your search."
      },
      {
        title: "Event Details",
        description: "Click on any event to see accommodation details, accessibility features, and contact information."
      }
    ],
    iep: [
      {
        title: "Goal Creator",
        description: "Select a goal area, describe your child's needs, and let our AI generate measurable IEP goals for you."
      },
      {
        title: "Goal Improver",
        description: "Paste an existing goal and get suggestions to make it more specific, measurable, and effective."
      },
      {
        title: "State Resources",
        description: "Access links to your state's special education resources, parent training centers, and advocacy organizations."
      }
    ],
    community: [
      {
        title: "Browse Topics",
        description: "Explore discussion categories like potty training, sleep, school navigation, and more."
      },
      {
        title: "Join Discussions",
        description: "Click on any topic to read posts from other parents and share your own experiences."
      },
      {
        title: "Helpful Resources",
        description: "Each topic includes curated blog links and articles from trusted sources."
      }
    ],
    profile: [
      {
        title: "Your Contribution Score",
        description: "Earn points by writing reviews, participating in discussions, and helping other families."
      },
      {
        title: "Settings",
        description: "Update your profile, notification preferences, and connection settings here."
      }
    ]
  };

  const pageTips = tips || defaultTips[pageName] || [];

  if (pageTips.length === 0) return null;

  return (
    <>
      {/* Help Button - positioned bottom-left to avoid "Made with Emergent" badge */}
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 left-6 w-12 h-12 bg-blue-600 text-white rounded-full shadow-lg hover:bg-blue-700 focus:outline-none focus:ring-4 focus:ring-blue-300 transition-all hover:scale-110 z-40 flex items-center justify-center"
        aria-label="Get help with this page"
      >
        <HelpCircle size={24} />
      </button>

      {/* Help Modal */}
      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center p-0 sm:p-4 z-50">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-md max-h-[80vh] overflow-hidden">
            {/* Header */}
            <div className="bg-blue-600 px-6 py-4 flex justify-between items-center">
              <div>
                <h3 className="text-white font-semibold text-lg">How to Use This Page</h3>
                <p className="text-blue-100 text-sm capitalize">{pageName} Guide</p>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="text-white hover:bg-blue-700 p-2 rounded-full"
                aria-label="Close help"
              >
                <X size={20} />
              </button>
            </div>

            {/* Tips Content */}
            <div className="p-6 overflow-y-auto max-h-96">
              <div className="space-y-4">
                {pageTips.map((tip, index) => (
                  <div
                    key={index}
                    className={`p-4 rounded-lg border-2 transition-colors ${
                      currentTip === index
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-blue-300'
                    }`}
                    onClick={() => setCurrentTip(index)}
                  >
                    <div className="flex items-start">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mr-3 ${
                        currentTip === index ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'
                      }`}>
                        <span className="text-sm font-medium">{index + 1}</span>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-1">{tip.title}</h4>
                        <p className="text-gray-600 text-sm">{tip.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Footer */}
            <div className="border-t border-gray-200 px-6 py-4 bg-gray-50">
              <button
                onClick={() => setIsOpen(false)}
                className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Got it!
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default HelpButton;
